import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Crown, Palette, Heart, FileText, Star, ShieldCheck, BookOpen, Trophy, MessageCircle, X } from 'lucide-react';
import { api, resolveAsset } from '../api/client';
import TemplateCard from '../components/TemplateCard';
import PromoBanner from '../components/PromoBanner';
import GraduationSeriesBanner from '../components/GraduationSeriesBanner';
import StoryShowcase from '../components/StoryShowcase';
import { getReviews } from '../data/reviews';
import testimonialShots from '../data/testimonials.json';
import { useT, useLang, useLocalField } from '../i18n';

// The story we're actively promoting right now: it leads the "most requested"
// section as a full-width banner (and is skipped in the grid below so it never
// shows twice). Set to a template slug to promote it, or null for no banner —
// the banner markup below is skipped entirely and the promoted story simply
// takes its normal place in the grid. (The World Cup promotion ran here until
// 2026-07-30.)
const PROMOTED_SLUG = null;

// Homepage-only marketing order for the popular grid. Featured templates not
// listed here append after, in catalog order; sortOrder still rules the
// category pages, so reshuffling this list never touches those.
const FEATURED_ORDER = [
  'worldcup-hero',
  'hero-spiderman',
  'hero-space-explorer',
  'hero-elsa',
  'milestone-potty',
  'milestone-dentist',
  'new-sibling-jealousy',
  'hero-barbie',
  'milestone-kindergarten',
  'hero-captain-majid',
  'fear-of-darkness',
];

// Browse-by-category tiles use a generated A5 book-mockup per top-level slug
// (the physical product we sell). Falls back to the category's own hero image.
const CATEGORY_BOOKS = {
  therapy: '/samples/book-therapy.png',
  birthday: '/samples/book-birthday.png',
  heroes: '/samples/book-heroes.png',
  milestones: '/samples/book-milestones.png',
};

// Sample book titles overlaid on each cover (kid-name stories). Rendered as
// real browser text — AI image models garble Arabic, so the covers are
// generated text-free and titled here for crisp, correct, editable type.
// (CATEGORY_TITLES moved inside component to use translation keys)

export default function Home() {
  const t = useT();
  const { lang } = useLang();
  const lf = useLocalField();
  const REVIEWS = useMemo(() => getReviews(lang), [lang]);
  const CATEGORY_TITLES = useMemo(() => ({
    therapy: t.categoryCoverTherapy,
    heroes: t.categoryCoverHeroes,
    birthday: t.categoryCoverBirthday,
    milestones: t.categoryCoverMilestones,
  }), [t]);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  // Graduation-series banner visibility is admin-controlled (Settings →
  // "بانر السلسلة"). Default true so it shows immediately and stays shown if the
  // fetch fails — matching the pre-toggle always-on behavior.
  const [gradBannerOn, setGradBannerOn] = useState(true);
  // Lightbox for the real-WhatsApp-screenshots proof gallery (index, or null).
  const [shotIndex, setShotIndex] = useState(null);
  // Auto-cycling carousel (shows 2 cards + a peek of the third). carIndex can
  // run one past the last slide into cloned head slides, then snaps back for a
  // seamless infinite loop.
  const shotCount = testimonialShots.length;
  const [carIndex, setCarIndex] = useState(0);
  const [carAnim, setCarAnim] = useState(true);
  const [carPaused, setCarPaused] = useState(false);

  useEffect(() => {
    api.get('/api/categories').then(setCategories).catch(() => {});
    api.get('/api/templates').then((d) => setTemplates(d.templates || [])).catch(() => {});
    api.get('/api/site-config').then((c) => setGradBannerOn(c?.gradBannerEnabled !== false)).catch(() => {});
  }, []);

  useEffect(() => {
    if (shotIndex === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShotIndex(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shotIndex]);

  // Advance the carousel automatically (paused on hover or while the lightbox
  // is open). Nothing to cycle with 2 or fewer slides.
  useEffect(() => {
    if (shotCount <= 2 || carPaused || shotIndex !== null) return undefined;
    const id = setInterval(() => setCarIndex((v) => v + 1), 3800);
    return () => clearInterval(id);
  }, [shotCount, carPaused, shotIndex]);

  // Seamless loop: once we've slid into the cloned head slides, jump back to the
  // real start with the transition disabled so the reset is invisible.
  useEffect(() => {
    if (carIndex >= shotCount) {
      const t = setTimeout(() => { setCarAnim(false); setCarIndex(0); }, 650);
      return () => clearTimeout(t);
    }
    if (!carAnim) {
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setCarAnim(true)));
      return () => cancelAnimationFrame(r);
    }
    return undefined;
  }, [carIndex, carAnim, shotCount]);

  const featured = templates.filter(tp => tp.isFeatured);
  const orderOf = (tp) => {
    const i = FEATURED_ORDER.indexOf(tp.slug);
    return i === -1 ? FEATURED_ORDER.length : i;
  };
  const popular = [...featured].sort((a, b) => orderOf(a) - orderOf(b));
  const promoted = popular.find((tp) => tp.slug === PROMOTED_SLUG);
  const popularGrid = popular.filter((tp) => tp !== promoted);

  return (
    <div className="home-page">
      {/* Mobile: promo sits at the very top. On desktop this instance is hidden
          and a second one renders under the popular grid (see below). */}
      <PromoBanner placement="mobile" />
      <section className="hero-banner">
        <h1 className="hero-headline">{t.heroHeadline}</h1>
        <p className="hero-subline">{t.heroSubline}</p>
        <a
          href="#categories"
          className="hero-cta submit-btn"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          {t.startCreating}
        </a>

        <div className="hero-social-proof">
          <div className="social-stat"><ShieldCheck size={18} /><span>{t.heroSocialReview}</span></div>
          <div className="social-stat"><BookOpen size={18} /><span>{t.heroSocialFormats}</span></div>
        </div>

        <div className="hero-features">
          <div className="hero-feature">
            <Crown className="hero-feature-icon" />
            <div>
              <p className="hero-feature-label">{t.featureHero}</p>
              <p className="hero-feature-desc">{t.featureHeroDesc}</p>
            </div>
          </div>
          <div className="hero-feature">
            <Palette className="hero-feature-icon" />
            <div>
              <p className="hero-feature-label">{t.featureIllustrations}</p>
              <p className="hero-feature-desc">{t.featureIllustrationsDesc}</p>
            </div>
          </div>
          <Link to="/therapeutic-approach" className="hero-feature hero-feature--link">
            <Heart className="hero-feature-icon" />
            <div>
              <p className="hero-feature-label">{t.featureTherapy}</p>
              <p className="hero-feature-desc">{t.featureTherapyDesc}</p>
            </div>
            <span className="hero-feature-more">{t.heroFeatureMore}</span>
          </Link>
        </div>
      </section>

      {gradBannerOn && <GraduationSeriesBanner />}

      {categories.length > 0 && (
        <section id="categories" className="home-section">
          <h2 className="section-title">{t.browseCategories}</h2>
          <div className="category-books">
            {categories.map((cat) => {
              const isTherapy = cat.slug === 'therapy';
              return (
                <Link
                  key={cat._id}
                  to={`/c/${cat.slug}`}
                  className={`category-book-tile${isTherapy ? ' category-book-tile--therapy' : ''}`}
                >
                  <div className="category-book-frame">
                    {isTherapy && (
                      <span className="category-book-badge">
                        <ShieldCheck size={14} aria-hidden="true" />
                        {t.categoryBadgeTherapy}
                      </span>
                    )}
                    <img
                      src={CATEGORY_BOOKS[cat.slug] || resolveAsset(cat.heroImageUrl)}
                      alt={lf(cat, 'nameAr')}
                      className="category-book-img"
                    />
                    {CATEGORY_TITLES[cat.slug] && (
                      <span className="category-book-title">{CATEGORY_TITLES[cat.slug]}</span>
                    )}
                  </div>
                  <span className="category-book-label">{lf(cat, 'nameAr')}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Real books, openable — replaced the single promoted-story banner that
          used to sit here. Cards link to each sample's /demo flip-through. */}
      <StoryShowcase />

      {(promoted || popularGrid.length > 0) && (
        <section className="home-section">
          <h2 className="section-title">{t.featuredTemplates}</h2>
          {promoted && (
            <Link to={`/create/${promoted.slug}`} className="promo-banner">
              <div className="promo-banner-media">
                <img src={resolveAsset(promoted.coverImageUrl)} alt={lf(promoted, 'nameAr')} />
              </div>
              <div className="promo-banner-body">
                <span className="promo-banner-badge"><Trophy size={15} aria-hidden="true" /> {t.promoBadgeNew}</span>
                <h3 className="promo-banner-title">{lf(promoted, 'nameAr')}</h3>
                {lf(promoted, 'taglineAr') && <p className="promo-banner-tagline">{lf(promoted, 'taglineAr')}</p>}
                <span className="promo-banner-cta">{t.promoStartCta}</span>
              </div>
            </Link>
          )}
          {popularGrid.length > 0 && (
            <div className="template-grid">
              {popularGrid.map((tp) => <TemplateCard key={tp._id} template={tp} />)}
            </div>
          )}
        </section>
      )}

      {/* Desktop: promo deal banner under the popular grid (hidden on mobile,
          where the top instance above is shown instead). */}
      <PromoBanner placement="desktop" />

      {/* Examples of stories — hidden for now (flip `false` to re-enable) */}
      {false && (
      <section className="home-section">
        <h2 className="section-title">{t.sampleStories}</h2>
        <div className="sample-stories-grid">
          {[
            { title: t.sampleTherapy, desc: t.sampleTherapyDesc, pdf: '/samples/story-therapy.pdf', color: 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)' },
            { title: t.sampleBirthday, desc: t.sampleBirthdayDesc, pdf: '/samples/story-birthday.pdf', color: 'linear-gradient(135deg, #ec4899 0%, #fbbf24 100%)' },
            { title: t.sampleHeroes, desc: t.sampleHeroesDesc, pdf: '/samples/story-heroes.pdf', color: 'linear-gradient(135deg, #06b6d4 0%, #6366f1 100%)' },
          ].map((s) => (
            <a key={s.pdf} href={s.pdf} target="_blank" rel="noopener noreferrer" className="sample-story-card">
              <div className="sample-story-icon" style={{ background: s.color }}>
                <FileText size={28} color="#fff" />
              </div>
              <div className="sample-story-body">
                <h3 className="sample-story-title">{s.title}</h3>
                <p className="sample-story-desc">{s.desc}</p>
                <span className="sample-story-link">{t.viewPdf}</span>
              </div>
            </a>
          ))}
        </div>
      </section>
      )}

      <section className="home-section">
        <h2 className="section-title">{t.reviewsSectionTitle}</h2>
        <div className="reviews-grid">
          {REVIEWS.slice(0, 6).map((r, i) => (
            <div key={i} className="review-card">
              <div className="review-header">
                <span className="review-avatar">{r.name.charAt(0)}</span>
                <div>
                  <span className="review-name">{r.name}</span>
                  <div className="review-stars">
                    {Array.from({ length: r.stars }, (_, j) => (
                      <Star key={j} size={14} fill="#f59e0b" color="#f59e0b" />
                    ))}
                    {Array.from({ length: 5 - r.stars }, (_, j) => (
                      <Star key={j + r.stars} size={14} color="#d1d5db" />
                    ))}
                  </div>
                </div>
              </div>
              <p className="review-text">{r.text}</p>
              <span className="review-meta">{r.template} &middot; {new Date(r.date).toLocaleDateString(lang === 'he' ? 'he' : 'ar')}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Real WhatsApp messages from parents — unedited proof (numbers blurred).
          Populated by server/scripts/buildTestimonials.js from screenshots in
          marketing/testimonials-raw/. Hidden until at least one image exists. */}
      {testimonialShots.length > 0 && (
        <section className="home-section">
          <h2 className="section-title">{t.waProofTitle}</h2>
          <p className="wa-proof-subtitle">
            <MessageCircle size={16} /> {t.waProofSubtitle}
          </p>
          <div
            className="wa-carousel"
            onMouseEnter={() => setCarPaused(true)}
            onMouseLeave={() => setCarPaused(false)}
          >
            <div
              className="wa-carousel-track"
              style={{ '--wa-i': carIndex, transition: carAnim ? undefined : 'none' }}
            >
              {(shotCount > 2 ? [...testimonialShots, ...testimonialShots.slice(0, 4)] : testimonialShots).map((shot, i) => (
                <div className="wa-slide" key={`${shot.src}-${i}`} aria-hidden={i >= shotCount}>
                  <button
                    type="button"
                    className="wa-proof-card"
                    onClick={() => setShotIndex(i % shotCount)}
                    aria-label={t.waProofView}
                    tabIndex={i >= shotCount ? -1 : 0}
                  >
                    <img src={shot.src} alt={t.waProofAlt} loading="lazy" width={shot.w} height={shot.h} />
                    <span className="wa-proof-zoom">{t.waProofView}</span>
                  </button>
                </div>
              ))}
            </div>

            {shotCount > 2 && (
              <>
                <button
                  type="button"
                  className="wa-car-arrow wa-car-prev"
                  onClick={() => {
                    if (carIndex <= 0) {
                      setCarAnim(false);
                      setCarIndex(shotCount);
                      requestAnimationFrame(() => requestAnimationFrame(() => { setCarAnim(true); setCarIndex(shotCount - 1); }));
                    } else {
                      setCarIndex((v) => v - 1);
                    }
                  }}
                  aria-label="Previous"
                >‹</button>
                <button
                  type="button"
                  className="wa-car-arrow wa-car-next"
                  onClick={() => setCarIndex((v) => v + 1)}
                  aria-label="Next"
                >›</button>
              </>
            )}
          </div>

          {shotCount > 2 && (
            <div className="wa-car-dots">
              {testimonialShots.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`wa-car-dot${(((carIndex % shotCount) + shotCount) % shotCount) === i ? ' is-active' : ''}`}
                  onClick={() => setCarIndex(i)}
                  aria-label={`${i + 1}`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {shotIndex !== null && createPortal(
        <div
          className="wa-lightbox"
          onClick={() => setShotIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <button className="wa-lightbox-close" onClick={() => setShotIndex(null)} aria-label={t.close || 'X'}>
            <X size={28} />
          </button>
          <img
            src={testimonialShots[shotIndex].src}
            alt={t.waProofAlt}
            className="wa-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
